import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowRight, Pause, Play } from "lucide-react";
import { api, BACKEND_URL } from "../../lib/api";
import { haptic } from "../../lib/haptics";

const img = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

const THEMES = {
  noir: {
    wrap: "bg-[#0B0B0B] text-[#FAF7F2]",
    overlay: "bg-gradient-to-r from-black/85 via-black/45 to-black/10",
    bottom: "bg-gradient-to-t from-black/80 to-transparent",
    eyebrow: "text-[var(--gold)] border-[#C8A96B]/40 bg-black/30",
    sub: "text-white/75",
    ghost: "!border-white/40 !text-white hover:!bg-white hover:!text-black",
    dot: "bg-white/30", dotOn: "bg-[var(--gold)]",
    arrow: "bg-white/10 hover:bg-[var(--gold)] text-white",
  },
  ivory: {
    wrap: "bg-[#FAF7F2] text-[#111111]",
    overlay: "bg-gradient-to-r from-[#FAF7F2]/95 via-[#FAF7F2]/60 to-transparent",
    bottom: "bg-gradient-to-t from-[#FAF7F2]/85 to-transparent",
    eyebrow: "text-[#A38243] border-[#A38243]/30 bg-white/60",
    sub: "text-[#4a443b]",
    ghost: "!border-[#111]/25 !text-[#111] hover:!bg-[#111] hover:!text-white",
    dot: "bg-black/20", dotOn: "bg-[#A38243]",
    arrow: "bg-white/70 hover:bg-[var(--gold)] hover:text-white text-[#111]",
  },
};

const textIn = (d) => ({ initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -12 }, transition: { duration: 0.6, delay: d, ease: [0.22, 1, 0.36, 1] } });

function SlideText({ s, t }) {
  return (
    <motion.div key={s.id} className="max-w-2xl" initial="initial" animate="animate" exit="exit">
      <h1 data-testid="hero-brand-h1" className={`text-[10px] md:text-[11px] uppercase tracking-[0.3em] mb-4 ${t.sub}`}>Locked In Forever — Wedding Websites, Planning &amp; Vendors</h1>
      {s.eyebrow && <motion.span {...textIn(0.05)} className={`inline-block text-[10px] md:text-[11px] uppercase tracking-[0.28em] px-3 py-1.5 rounded-full border backdrop-blur ${t.eyebrow}`}>{s.eyebrow}</motion.span>}
      <motion.h2 {...textIn(0.15)} data-testid="hero-slide-title" className="font-serif font-light text-4xl sm:text-5xl lg:text-7xl leading-[1.02] mt-5 tracking-tight">{s.headline}</motion.h2>
      {s.accent && <motion.p {...textIn(0.25)} className="font-script text-[var(--gold)] text-5xl sm:text-6xl lg:text-8xl leading-[0.95] mt-1">{s.accent}</motion.p>}
      {s.subhead && <motion.p {...textIn(0.35)} className={`mt-6 text-base md:text-lg max-w-lg leading-relaxed ${t.sub}`}>{s.subhead}</motion.p>}
      <motion.div {...textIn(0.45)} className="mt-8 flex flex-wrap gap-3">
        {s.cta_label && <Link to={s.cta_link || "/signup"} onClick={() => { if (s.kind === "couple") api.post(`/public/hero-click/${s.id.slice(2)}`).catch(() => {}); }} data-testid="hero-slide-cta-button" className="btn-gold">{s.cta_label} <ArrowRight className="w-4 h-4" /></Link>}
        {s.kind !== "couple" && <Link to="/site/emmanuel-and-jennifer" data-testid="hero-demo-btn" className={`btn-outline-gold ${t.ghost}`}>View demo site</Link>}
      </motion.div>
    </motion.div>
  );
}

export default function HeroSlider({ aside = null, onTheme }) {
  const [cfg, setCfg] = useState(null);
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    api.get("/site/homepage").then((r) => { setCfg(r.data); onTheme?.(r.data.theme); }).catch(() => setCfg({ theme: "noir", autoplay_seconds: 6, slides: [] }));
  }, [onTheme]);

  const slides = useMemo(() => {
    const base = cfg?.slides || [], extra = cfg?.featured_couples || [], out = [];
    for (let k = 0; k < Math.max(base.length, extra.length); k++) { if (base[k]) out.push(base[k]); if (extra[k]) out.push(extra[k]); }
    return out;
  }, [cfg]);
  const n = slides.length;
  const go = useCallback((d) => { if (n) { setI((x) => (x + d + n) % n); haptic(); } }, [n]);

  useEffect(() => {
    if (paused || n < 2) return;
    const t = setTimeout(() => setI((x) => (x + 1) % n), (cfg?.autoplay_seconds || 6) * 1000);
    return () => clearTimeout(t);
  }, [i, paused, n, cfg]);

  const t = THEMES[cfg?.theme] || THEMES.noir;
  if (!cfg) return <section className="h-[88vh] min-h-[560px] bg-[#0B0B0B]" />;
  const s = slides[i];

  return (
    <section data-testid="hero-slider-container" data-theme={cfg.theme}
      className={`relative h-[88vh] min-h-[680px] lg:min-h-[780px] max-h-[1000px] overflow-hidden ${t.wrap}`}
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <AnimatePresence initial={false}>
        {s && (
          <motion.div key={s.id} className="absolute inset-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.1 }}
            drag="x" dragConstraints={{ left: 0, right: 0 }} dragElastic={0.18}
            onDragEnd={(_, info) => { if (info.offset.x < -60) go(1); else if (info.offset.x > 60) go(-1); }}>
            <motion.img src={img(s.image_url)} alt="" draggable="false" className="absolute inset-0 w-full h-full object-cover select-none" style={{ objectPosition: `${s.focus_x ?? 50}% ${s.focus_y ?? 50}%`, transformOrigin: `${s.focus_x ?? 50}% ${s.focus_y ?? 50}%` }}
              initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: (cfg.autoplay_seconds || 6) + 2, ease: "linear" }} />
            <div className={`absolute inset-0 ${t.overlay}`} />
            <div className={`absolute inset-x-0 bottom-0 h-1/3 ${t.bottom}`} />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative z-10 h-full container-wide px-5 md:px-12 flex items-center pointer-events-none">
        <div className="pointer-events-auto w-full pt-14 md:pt-16 pb-16">
          <motion.img src="/brand/logo-full.png" alt="LockedInForever — Forever Starts Here" data-testid="hero-logo" draggable="false"
            initial={{ opacity: 0, y: 12, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="h-24 sm:h-28 lg:h-32 w-auto mb-5 md:mb-6 drop-shadow-[0_6px_24px_rgba(0,0,0,0.45)] select-none" />
          <AnimatePresence mode="wait">{s && <SlideText s={s} t={t} />}</AnimatePresence>
        </div>
      </div>

      {aside && <div className="hidden lg:block absolute right-12 bottom-24 z-10">{aside}</div>}

      {n > 1 && (
        <div className="absolute z-10 bottom-6 md:bottom-8 inset-x-0 container-wide px-5 md:px-12 flex items-center gap-4">
          <div className="flex items-center gap-2">
            {slides.map((sl, k) => (
              <button key={sl.id} onClick={() => { setI(k); haptic(); }} aria-label={`Slide ${k + 1}`} data-testid={`hero-slider-dot-${k}`}
                className={`h-1.5 rounded-full transition-[width,background-color] duration-500 ${k === i ? `w-10 ${t.dotOn}` : `w-4 ${t.dot}`}`} />
            ))}
          </div>
          <button onClick={() => setPaused((p) => !p)} data-testid="hero-slider-pause" aria-label="Pause" className="hidden md:flex w-8 h-8 rounded-full items-center justify-center opacity-70 hover:opacity-100">
            {paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
          </button>
          <div className="ml-auto hidden md:flex gap-2">
            <button onClick={() => go(-1)} data-testid="hero-slider-prev-btn" aria-label="Previous" className={`w-11 h-11 rounded-full flex items-center justify-center backdrop-blur transition-colors ${t.arrow}`}><ChevronLeft className="w-5 h-5" /></button>
            <button onClick={() => go(1)} data-testid="hero-slider-next-btn" aria-label="Next" className={`w-11 h-11 rounded-full flex items-center justify-center backdrop-blur transition-colors ${t.arrow}`}><ChevronRight className="w-5 h-5" /></button>
          </div>
        </div>
      )}
    </section>
  );
}
